import React, { useState, useEffect } from "react";
import { Outlet, useNavigate, useLocation, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, authHeaders } from "@/lib/api";
import { LayoutDashboard, Tag, QrCode, Store as StoreIcon, History, LogOut, Ticket, Loader2, MessageSquare, Star, ShieldCheck, Users, Gift, BarChart3, Send } from "lucide-react";
import OnboardingTutorial, { tutorialDone, resetTutorial } from "@/components/OnboardingTutorial";
import InboxBell from "@/components/InboxBell";

const items = [
  { to: "/vendor", label: "Dashboard", icon: LayoutDashboard, testid: "vnav-dashboard", exact: true },
  { to: "/vendor/stats", label: "Statistiche", icon: BarChart3, testid: "vnav-stats", desktopOnly: true },
  { to: "/vendor/offers", label: "Le mie offerte", icon: Tag, testid: "vnav-offers" },
  { to: "/vendor/validate", label: "Valida in cassa", icon: QrCode, testid: "vnav-validate" },
  { to: "/vendor/coupon-clients", label: "Coupon clienti", icon: Ticket, testid: "vnav-coupon-clients" },
  { to: "/vendor/reviews", label: "Recensioni", icon: Star, testid: "vnav-reviews", badgeKey: "reviews" },
  { to: "/vendor/messages", label: "Messaggi", icon: MessageSquare, testid: "vnav-messages",
    children: [
      { to: "/vendor/messages", label: "Messaggi inviati", icon: Send, testid: "vnav-messages-sent", exact: true },
      { to: "/vendor/messages/loyalty", label: "Messaggi Fedeltà", icon: Gift, testid: "vnav-messages-loyalty" },
    ] },
  { to: "/vendor/followers", label: "Follower", icon: Users, testid: "vnav-followers", badgeKey: "followers",
    children: [{ to: "/vendor/coupons", label: "Coupon predefiniti", icon: Gift, testid: "vnav-coupons" }] },
  { to: "/vendor/history", label: "Incassi", icon: History, testid: "vnav-history" },
  { to: "/vendor/store", label: "Il mio negozio", icon: StoreIcon, testid: "vnav-store" },
  { to: "/vendor/verification", label: "Verifica", icon: ShieldCheck, testid: "vnav-verification" },
];

const SEEN_BY_PATH = [
  { prefix: "/vendor/reviews", category: "reviews" },
  { prefix: "/vendor/followers", category: "followers" },
  { prefix: "/vendor/coupon-clients", category: "coupons" },
];

export default function VendorLayout() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { vendor, ready, vendorLogout } = useAuth();
  const [tutOpen, setTutOpen] = useState(false);
  const [badges, setBadges] = useState({ reviews: 0, followers: 0, coupons: 0 });

  const loadBadges = React.useCallback(() => {
    if (!vendor) return;
    api.get("/vendor/badges", { headers: authHeaders("vendor") })
      .then((r) => setBadges(r.data || {})).catch(() => {});
  }, [vendor]);

  useEffect(() => {
    if (!vendor) return;
    loadBadges();
    const id = setInterval(loadBadges, 25000);
    const onRefresh = () => loadBadges();
    window.addEventListener("kn-vendor-badges-refresh", onRefresh);
    return () => { clearInterval(id); window.removeEventListener("kn-vendor-badges-refresh", onRefresh); };
  }, [vendor, loadBadges]);

  // Mark a category as seen when the vendor opens its page, then refresh badges.
  useEffect(() => {
    if (!vendor) return;
    const match = SEEN_BY_PATH.find((s) => pathname.startsWith(s.prefix));
    if (match) {
      api.post("/vendor/badges/seen", { category: match.category }, { headers: authHeaders("vendor") })
        .then(() => loadBadges()).catch(() => {});
    }
  }, [pathname, vendor, loadBadges]);

  useEffect(() => {
    if (ready && vendor && pathname === "/vendor" && !tutorialDone("vendor")) {
      const t = setTimeout(() => setTutOpen(true), 900);
      return () => clearTimeout(t);
    }
  }, [ready, vendor, pathname]);

  useEffect(() => {
    const replay = () => { resetTutorial("vendor"); navigate("/vendor"); setTimeout(() => setTutOpen(true), 400); };
    window.addEventListener("kn-replay-tutorial-vendor", replay);
    return () => window.removeEventListener("kn-replay-tutorial-vendor", replay);
  }, [navigate]);

  if (!ready) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#E63946]" /></div>;
  if (!vendor) return <Navigate to="/vendor/login" replace />;

  const isActive = (it) => (it.exact ? pathname === it.to : pathname.startsWith(it.to));

  const logout = () => { vendorLogout(); navigate("/vendor/login"); };

  return (
    <div className="min-h-screen bg-slate-50 lg:pl-64">
      {/* Sidebar (desktop) */}
      <aside className="hidden lg:flex flex-col fixed inset-y-0 left-0 w-64 bg-slate-900 text-white p-5">
        <div className="flex items-center gap-2 mb-8">
          <div className="h-9 w-9 rounded-xl kn-gradient flex items-center justify-center"><Ticket className="h-5 w-5" /></div>
          <span className="font-extrabold text-lg">Koupo<span className="text-[#E63946]">Now</span></span>
        </div>
        <nav className="space-y-1 flex-1">
          {items.map((it) => {
            const Icon = it.icon;
            return (
              <React.Fragment key={it.to}>
                <button data-testid={it.testid} onClick={() => navigate(it.to)}
                  className={`w-full flex items-center gap-3 px-3.5 h-11 rounded-xl font-semibold text-sm transition-colors ${
                    isActive(it) ? "kn-gradient text-white" : "text-slate-300 hover:bg-slate-800"
                  }`}>
                  <Icon className="h-5 w-5" /> {it.label}
                  {it.badgeKey && badges[it.badgeKey] > 0 && (
                    <span data-testid={`${it.testid}-badge`} className="ml-auto min-w-[20px] h-5 px-1.5 rounded-full kn-gradient text-white text-[11px] font-extrabold flex items-center justify-center">
                      {badges[it.badgeKey] > 9 ? "9+" : badges[it.badgeKey]}
                    </span>
                  )}
                </button>
                {(it.children || []).map((ch) => {
                  const ChIcon = ch.icon;
                  const chActive = ch.exact ? pathname === ch.to : pathname.startsWith(ch.to);
                  return (
                    <button key={ch.to} data-testid={ch.testid} onClick={() => navigate(ch.to)}
                      className={`w-full flex items-center gap-2.5 pl-9 pr-3.5 h-10 rounded-xl font-medium text-[13px] transition-colors ${
                        chActive ? "bg-slate-800 text-white" : "text-slate-400 hover:bg-slate-800/60"
                      }`}>
                      <ChIcon className="h-4 w-4" /> {ch.label}
                    </button>
                  );
                })}
              </React.Fragment>
            );
          })}
        </nav>
        <div className="border-t border-slate-800 pt-3">
          <div className="px-3 mb-2 min-w-0 flex items-center gap-2">
            <div className="min-w-0 flex-1">
              <p className="text-sm font-bold truncate">{vendor.store_name || "Negozio"}</p>
              <p className="text-xs text-slate-400 truncate">{vendor.email}</p>
            </div>
            <InboxBell role="vendor" />
          </div>
          <button onClick={logout} data-testid="vendor-logout" className="w-full flex items-center gap-3 px-3.5 h-11 rounded-xl text-slate-300 hover:bg-slate-800 font-semibold text-sm">
            <LogOut className="h-5 w-5" /> Esci
          </button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <header className="lg:hidden sticky top-0 z-30 bg-slate-900 text-white px-4 h-14 flex items-center gap-2">
        <div className="h-8 w-8 rounded-lg kn-gradient flex items-center justify-center"><Ticket className="h-4 w-4" /></div>
        <span className="font-extrabold">KoupoNow</span>
        <span className="ml-auto text-sm text-slate-300 truncate max-w-[40%]">{vendor.store_name}</span>
        <InboxBell role="vendor" />
        <button onClick={logout} className="ml-2"><LogOut className="h-5 w-5" /></button>
      </header>

      <main className="p-4 sm:p-6 lg:p-8 pb-24 lg:pb-8 max-w-5xl mx-auto">
        {vendor.verification_status && vendor.verification_status !== "approved" && !pathname.startsWith("/vendor/verification") && (
          <button onClick={() => navigate("/vendor/verification")} data-testid="verif-banner"
            className="w-full mb-4 flex items-center gap-3 px-4 py-3 rounded-2xl bg-amber-50 border border-amber-200 text-left hover:bg-amber-100 transition-colors">
            <ShieldCheck className="h-5 w-5 text-amber-600 shrink-0" />
            <span className="text-sm font-semibold text-amber-800">
              {vendor.verification_status === "submitted"
                ? "Verifica in corso: le tue offerte saranno visibili dopo l'approvazione."
                : vendor.verification_status === "rejected"
                ? "Verifica non superata. Tocca per correggere e reinviare i documenti."
                : "Verifica la tua identità per pubblicare offerte. Tocca per iniziare."}
            </span>
          </button>
        )}
        <Outlet />
      </main>

      {/* Mobile bottom nav */}
      <nav className="lg:hidden fixed bottom-0 inset-x-0 z-30 bg-white/95 backdrop-blur border-t border-slate-100 grid grid-cols-9">
        {items.filter((it) => !it.desktopOnly).map((it) => {
          const Icon = it.icon;
          return (
            <button key={it.to} data-testid={`m-${it.testid}`} onClick={() => navigate(it.to)}
              className={`relative flex flex-col items-center gap-0.5 py-2 text-[9px] font-semibold ${isActive(it) ? "text-[#E63946]" : "text-slate-400"}`}>
              {it.badgeKey && badges[it.badgeKey] > 0 && (
                <span className="absolute top-1 right-[18%] min-w-[15px] h-[15px] px-1 rounded-full kn-gradient text-white text-[8px] font-extrabold flex items-center justify-center">
                  {badges[it.badgeKey] > 9 ? "9+" : badges[it.badgeKey]}
                </span>
              )}
              <Icon className="h-5 w-5" /> {it.label.split(" ")[0]}
            </button>
          );
        })}
      </nav>
      {tutOpen && <OnboardingTutorial role="vendor" onClose={() => setTutOpen(false)} />}
    </div>
  );
}
